import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Landmark, QrCode, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { API_ENDPOINTS, PAYMENT_METHOD_LABELS } from '@/utils/constants';
import { Transaction, TransactionListResponse, ApiResponse, PageMetadata } from '@/services/types';
import { Alert, Badge, Button, LoadingSkeleton, SearchPill, SegmentedControl } from '@/components/ui';
import { DateRangePicker, DateRange } from '@/components/ui/DateRangePicker';
import { StoreHeader } from '@/components/layout/StoreHeader';
import { QueuedSalesBanner } from '@/features/checkout/QueuedSalesBanner';
import { formatCurrency } from '@/utils/formatters';
import { kindOf, rangeFor, toLocalDateTimeParams, type RangeKind } from '@/utils/dateRange';

interface TransactionHistoryProps {
    selected: string | null;
    onSelect: (receiptNumber: string) => void;
    autoSelectFirst?: boolean;
    className?: string;
}

const methodIcon = (bankCode?: string) => {
    const c = (bankCode ?? '').toLowerCase();
    if (c.includes('qris')) return QrCode;
    if (c.includes('epayment') || c.includes('wallet')) return Wallet;
    return Landmark;
};
/** epayment → rWallet, qris → QRIS, else the raw bank code */
const methodLabel = (bankCode?: string, fallback = '') => {
    const c = (bankCode ?? '').toUpperCase();
    if (!c) return fallback;
    if (c.includes('EPAYMENT') || c.includes('WALLET')) return 'rWallet';
    return PAYMENT_METHOD_LABELS[c] ?? bankCode ?? fallback;
};

/** O9 transaction list with range control, search and queued-sale banner. */
export const TransactionHistory: React.FC<TransactionHistoryProps> = ({ selected, onSelect, autoSelectFirst, className }) => {
    const { t } = useTranslation();
    const { selectedShop } = useAppSelector((state) => state.auth);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [pageMetadata, setPageMetadata] = useState<PageMetadata | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>(() => rangeFor('today'));
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 20;

    useEffect(() => {
        const id = setTimeout(() => { setSearchQuery(searchInput.trim()); setCurrentPage(0); }, 300);
        return () => clearTimeout(id);
    }, [searchInput]);

    useEffect(() => {
        if (selectedShop) fetchTransactions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedShop, dateRange, searchQuery, currentPage]);

    const fetchTransactions = async () => {
        if (!selectedShop) return;
        setLoading(true);
        setError(null);
        try {
            const { startDate, endDate } = toLocalDateTimeParams(dateRange);
            const params = new URLSearchParams({ startDate, endDate, page: currentPage.toString(), size: pageSize.toString() });
            if (searchQuery) params.append('q', searchQuery);
            const response = await api.get<ApiResponse<TransactionListResponse>>(`${API_ENDPOINTS.TRANSACTIONS(selectedShop.shopId)}?${params.toString()}`);
            const data = response.data.data;
            const content = data.content || [];
            setTransactions((prev) => (currentPage === 0 ? content : [...prev, ...content]));
            setPageMetadata(data.page);
            if (autoSelectFirst && currentPage === 0 && !selected && content[0]) onSelect(content[0].receiptNumber);
        } catch (err) {
            const message = getErrorMessage(err);
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (!selectedShop) return null;

    const rangeKind = kindOf(dateRange);
    const setKind = (k: RangeKind) => { setDateRange(rangeFor(k)); setCurrentPage(0); };
    const badgeFor = (tx: Transaction) =>
        tx.isVoided ? <Badge tone="danger">{t('transactions.cancelled')}</Badge>
        : tx.invoice?.status === 'WAITINGFORPAYMENT' ? <Badge tone="warning">{t('transactions.pending')}</Badge>
        : null;

    return (
        <div className={clsx('flex min-w-0 flex-1 flex-col gap-3 px-5 pt-2.5 lg:px-5 lg:pt-[22px]', className)}>
            <StoreHeader className="lg:hidden" />
            <div className="flex items-center gap-2.5">
                <h1 className="flex-1 text-2xl font-black lg:text-[26px]">{t('nav.transactions')}</h1>
                <DateRangePicker value={dateRange} onChange={(r) => { setDateRange(r); setCurrentPage(0); }} className={clsx(rangeKind && 'hidden lg:flex')} />
            </div>
            <SearchPill size="md" value={searchInput} onChange={setSearchInput} placeholder={t('transactions.search')} className="!flex-none" />
            <SegmentedControl fullWidth value={rangeKind ?? 'today'} onChange={setKind} options={[
                { value: 'today', label: t('dashboard.ranges.today') },
                { value: 'week', label: t('dashboard.ranges.week') },
                { value: 'month', label: t('dashboard.ranges.month') },
            ]} />
            {pageMetadata && <p className="px-0.5 text-[13px] font-bold text-ink-muted tnum">{t('transactions.count', { count: pageMetadata.totalElements })}</p>}
            <QueuedSalesBanner />

            <div className="min-h-0 flex-1 overflow-y-auto pb-5">
                {error && <Alert variant="error" message={error} className="mb-2" />}
                {loading && transactions.length === 0 && <div className="flex flex-col gap-2">{[1, 2, 3, 4, 5].map((i) => <LoadingSkeleton key={i} height={60} />)}</div>}
                {!loading && transactions.length === 0 && <p className="py-12 text-center text-sm text-ink-muted">{t('transactions.noTransactions')}</p>}
                <div className="flex flex-col gap-1">
                    {transactions.map((tx) => {
                        const Icon = methodIcon(tx.invoice?.bankCode);
                        const active = tx.receiptNumber === selected;
                        const time = new Date(tx.createdAt).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
                        return (
                            <button
                                key={tx.id}
                                type="button"
                                onClick={() => onSelect(tx.receiptNumber)}
                                className={clsx('flex items-center gap-3 rounded-md px-3 py-2.5 text-left', active ? 'border-2 border-primary-500 bg-surface-card shadow-sm' : 'border-2 border-transparent bg-surface-card hover:bg-surface-sunken')}
                            >
                                <span className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', active ? 'bg-brandsoft text-ink-brand' : 'bg-surface-sunken text-ink-secondary')}><Icon className="h-[18px] w-[18px]" /></span>
                                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                    <span className="truncate text-sm font-extrabold">{tx.receiptNumber}</span>
                                    <span className="truncate text-xs text-ink-muted">{time} · {methodLabel(tx.invoice?.bankCode, tx.type)} · {tx.cashierName}{tx.customerName ? ` · ${tx.customerName}` : ''}</span>
                                </span>
                                <span className="flex flex-col items-end gap-1">
                                    <span className="text-[15px] font-black tnum">{formatCurrency(tx.pointsRedeemed > 0 ? tx.amountDue : tx.grandTotal)}</span>
                                    {badgeFor(tx)}
                                </span>
                            </button>
                        );
                    })}
                </div>
                {pageMetadata && !pageMetadata.last && (
                    <div className="flex justify-center pt-3">
                        <Button variant="outline" onClick={() => setCurrentPage((p) => p + 1)} loading={loading}>{t('transactions.loadMore')}</Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TransactionHistory;
